import classNames from 'classnames';
import InfoIcon from '../icons/InfoIcon';
import ErrorIcon from '../icons/ErrorIcon';
import WarningIcon from '../icons/WarningIcon';
import { IconProps } from '../icons/types';

type StatusBoxType = 'info' | 'error' | 'warning';

export type DivProps = React.HTMLAttributes<HTMLDivElement>;

type StatusBoxProps = DivProps & {
  type: StatusBoxType;
};

const iconByType: Record<StatusBoxType, React.ComponentType<IconProps>> = {
  info: InfoIcon,
  error: ErrorIcon,
  warning: WarningIcon,
};

const colorByType: Record<StatusBoxType, { bg: string; border: string; text: string }> = {
  info: {
    bg: 'bg-[color-mix(in_oklch,var(--color-info)_10%,white)]',
    border: 'border-info',
    text: 'text-info',
  },
  error: {
    bg: 'bg-[color-mix(in_oklch,var(--color-error)_10%,white)]',
    border: 'border-error',
    text: 'text-error',
  },
  warning: {
    bg: 'bg-[color-mix(in_oklch,var(--color-warning)_10%,white)]',
    border: 'border-warning',
    text: 'text-warning',
  },
};

function StatusBox({ children, className, type, ...props }: StatusBoxProps) {
  const Icon = iconByType[type];
  const { bg, border, text } = colorByType[type];

  return (
    <div
      {...props}
      className={classNames('flex rounded-lg p-4 gap-2 border', bg, border, className)}
    >
      <span className={text}>
        <Icon size="md" />
      </span>
      <div className="w-full text-sm mt-0.5 min-w-0">{children}</div>
    </div>
  );
}

export function InfoBox({ ...props }: DivProps) {
  return <StatusBox {...props} type="info" />;
}

export function ErrorBox({ ...props }: DivProps) {
  return <StatusBox {...props} type="error" />;
}

export function WarningBox({ ...props }: DivProps) {
  return <StatusBox {...props} type="warning" />;
}
